/*
  rs-design-save.js
  Saved designs, part 1 (2026-10-13): every custom sign design is kept on the
  customer's own phone or computer while they work, and comes back when the
  page reloads (phones drop pages when memory runs low, or when the customer
  leaves to pick a logo from Photos) or when they return later.

  Loaded by snippets/rs-customize-popup.liquid. Each designer section
  registers itself at the end of its script (queue pattern, load order does
  not matter):

    (window.RSDesignQueue = window.RSDesignQueue || []).push({
      root: 'rs-csq',               // designer root id
      st: st,                       // the designer's state object
      render: render,               // redraws everything from st + fields
      sync: function () { ... },    // optional: closure flags that follow st
      ready: function () { ... }    // optional: true once symbols are in
    });

  What is kept (one design per sign page, 30 days, nothing leaves the device):
    - st, minus details rebuilt from the uploaded file itself
      (graphic*, design*, image*) and passing layout UI state (lay*)
    - st.lay (exact moved/resized spots) and st.layTouched, put back last,
      after the designer has re-checked the layout for the restored words
    - the typed fields: properties[...] text, selects, quantity, checkboxes
    - the uploaded logo or design file (IndexedDB), put back into its own
      file input so it still uploads with the order
  Hidden properties[_...] inputs (SKU, _Layout, _Preview image) are derived
  and never saved.

  Storage: IndexedDB "rs-designs" (stores drafts + files). If IndexedDB is
  not available (some private windows), localStorage keeps the design
  without the file.

  Skipped: links with ?rs_... ("Customize this sign" from a regular sign)
  load that sign instead. A change made before the saved design is back
  wins over the saved one.

  Page bits (styles in the snippet): "Picked up where you left off. Start
  over" line under the window title; "Customize now" becomes "Continue my
  design"; the note under the button says when it was saved, with a
  "Start a new design" button.

  Part 3 (2026-10-13): order this sign again.
    - At add to cart the form gets a hidden properties[_Design link]:
      /products/<handle>?design=j<code>, where <code> is the same design as
      above (st, typed fields, layout, file name) as base64url JSON. The
      underscore keeps it out of the cart, checkout and customer emails'
      property lists; the admin order shows it.
    - The order emails add the uploaded file's address as &f=<cdn url>
      (Settings > Notifications, "Order this sign again" button).
    - Opening the link: the window opens (#customize is added at load), the
      design is put back instead of the device copy, the file comes back
      from cdn.shopify.com, design/f leave the address, and the design is
      saved on this device like any other.
    - A link is outside data: every value is checked against the page
      before use (button choices must be the page's own, other words may
      not carry quotes or angle brackets, typed text loses < > `).
*/
(function () {
  'use strict';

  if (window.RSDesignSave) return;

  var VERSION = 1;
  var KEEP_MS = 30 * 24 * 60 * 60 * 1000;
  var SAVE_DELAY = 600;
  var READY_WAIT = 8000;
  var FONT_WAIT = 3000;
  var FILE_WAIT = 5000;
  var DB_NAME = 'rs-designs';
  var LS_PREFIX = 'rs-design:';
  /* Rebuilt from the uploaded file, or passing layout UI state */
  var SKIP_RE = /^(graphic|design|image|lay)/;
  /* Choice buttons whose pressed look only changes on click: data-* name
     -> st key (all 5 designers). The others follow st in render(). */
  var PRESS = {
    size: 'size', scheme: 'scheme', r: 'r', hole: 'hole', proof: 'proof',
    gp: 'placement', tc: 'msgColor', ac: 'arrowColor', sd: 'stopDesign',
    tdir: 'dir', s: 's', pos: 'pos', ip: 'imgArea', mount: 'mount'
  };
  /* Part 3: design link */
  var LINK_VERSION = 1;
  var LINK_NAME = 'properties[_Design link]';
  var FILE_HOST = 'https://cdn.shopify.com/';
  var LINK_FILE_WAIT = 10000;
  /* Words that are not a page choice: no quotes, angle brackets, backticks
     or backslashes, and no script addresses */
  var PLAIN_RE = /^[^<>`\\"']{0,300}$/;
  var SCRIPT_RE = /(javascript|vbscript|data)\s*:/i;
  var KEY_RE = /^[A-Za-z0-9_-]{1,40}$/;
  var CHECK_SVG = '<svg class="rs-czp-ok" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.2 7.6 5.6 11 11.8 3.4" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---------- storage ---------- */

  function pageKey() {
    var m = /\/products\/([^\/?#]+)/.exec(location.pathname);
    return m ? decodeURIComponent(m[1]) : location.pathname;
  }

  var dbPromise = null;

  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(function (resolve, reject) {
      var req;
      if (!window.indexedDB) { reject(new Error('no indexedDB')); return; }
      try { req = indexedDB.open(DB_NAME, 1); } catch (e) { reject(e); return; }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts');
        if (!db.objectStoreNames.contains('files')) db.createObjectStore('files');
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
      req.onblocked = function () { reject(new Error('blocked')); };
    });
    return dbPromise;
  }

  function idb(storeName, mode, fn) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(storeName, mode);
        var req = fn(tx.objectStore(storeName));
        tx.oncomplete = function () { resolve(req ? req.result : undefined); };
        tx.onerror = tx.onabort = function () { reject(tx.error); };
      });
    });
  }

  var IDB = {
    get: function (k) { return idb('drafts', 'readonly', function (os) { return os.get(k); }); },
    put: function (k, v) { return idb('drafts', 'readwrite', function (os) { return os.put(v, k); }); },
    del: function (k) {
      return idb('drafts', 'readwrite', function (os) { os.delete(k); })
        .then(function () { return idb('files', 'readwrite', function (os) { os.delete(k); }); });
    },
    getFile: function (k) { return idb('files', 'readonly', function (os) { return os.get(k); }); },
    putFile: function (k, v) { return idb('files', 'readwrite', function (os) { return os.put(v, k); }); },
    delFile: function (k) { return idb('files', 'readwrite', function (os) { os.delete(k); }); },
    /* Drop designs older than 30 days (all sign pages) */
    prune: function () {
      var cut = Date.now() - KEEP_MS;
      var old = [];
      return idb('drafts', 'readonly', function (os) {
        var req = os.openCursor();
        req.onsuccess = function () {
          var c = req.result;
          if (!c) return;
          if (!c.value || !c.value.t || c.value.t < cut) old.push(c.key);
          c.continue();
        };
        return null;
      }).then(function () {
        return Promise.all(old.map(function (k) { return IDB.del(k); }));
      });
    }
  };

  var LS = {
    get: function (k) {
      try { return Promise.resolve(JSON.parse(localStorage.getItem(LS_PREFIX + k) || 'null')); }
      catch (e) { return Promise.resolve(null); }
    },
    put: function (k, v) {
      try { localStorage.setItem(LS_PREFIX + k, JSON.stringify(v)); } catch (e) {}
      return Promise.resolve();
    },
    del: function (k) {
      try { localStorage.removeItem(LS_PREFIX + k); } catch (e) {}
      return Promise.resolve();
    },
    getFile: function () { return Promise.resolve(null); },
    putFile: function () { return Promise.resolve(); },
    delFile: function () { return Promise.resolve(); },
    prune: function () { return Promise.resolve(); }
  };

  var storePromise = null;

  function store() {
    if (!storePromise) {
      storePromise = openDb().then(function () { return IDB; }, function () { return LS; });
    }
    return storePromise;
  }

  /* ---------- helpers ---------- */

  function clone(v) { return v === undefined ? v : JSON.parse(JSON.stringify(v)); }

  function plain(v) {
    if (v === null) return true;
    var t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') return true;
    if (Array.isArray(v)) return true;
    return t === 'object' && Object.prototype.toString.call(v) === '[object Object]';
  }

  function fire(el, type) {
    var ev;
    try { ev = new Event(type, { bubbles: true }); }
    catch (e) { ev = document.createEvent('Event'); ev.initEvent(type, true, true); }
    el.dispatchEvent(ev);
  }

  function wait(test, ms) {
    return new Promise(function (resolve) {
      var end = Date.now() + ms;
      (function tick() {
        var ok = false;
        try { ok = test(); } catch (e) {}
        if (ok || Date.now() > end) resolve(ok);
        else setTimeout(tick, 80);
      })();
    });
  }

  function fontsIn() {
    if (!document.fonts || !document.fonts.ready) return Promise.resolve();
    return Promise.race([
      document.fonts.ready,
      new Promise(function (r) { setTimeout(r, FONT_WAIT); })
    ]).catch(function () {});
  }

  function readBuf(file) {
    if (file.arrayBuffer) return file.arrayBuffer();
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onload = function () { resolve(fr.result); };
      fr.onerror = function () { reject(fr.error); };
      fr.readAsArrayBuffer(file);
    });
  }

  function fileSig(f) { return f ? [f.name, f.size, f.lastModified].join('|') : ''; }

  function when(t) {
    var d = new Date(t);
    var now = new Date();
    var time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    var day0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (t >= day0) return 'today at ' + time;
    if (t >= day0 - 864e5) return 'yesterday at ' + time;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at ' + time;
  }

  function deviceWord() {
    var mm = window.matchMedia;
    if (mm && mm('(max-width: 749px)').matches) return 'phone';
    if (mm && mm('(pointer: coarse)').matches) return 'tablet';
    return 'computer';
  }

  /* ---------- design link (part 3) ---------- */

  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(location.search);
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch (e) { return ''; }
  }

  /* Read once at load. The window opens on #customize (snippet), so it is
     added before the snippet looks at the address. */
  var LINK = { code: param('design'), file: '', used: false };
  if (LINK.code) {
    LINK.file = param('f');
    if (location.hash !== '#customize') {
      try { history.replaceState(history.state, '', location.pathname + location.search + '#customize'); } catch (e) {}
    }
  }

  /* design and f leave the address once the sign is back */
  function cleanAddress() {
    var q = location.search.replace(/^\?/, '').split('&').filter(function (p) {
      return p && !/^(design|f)=/.test(p);
    });
    try { history.replaceState(history.state, '', location.pathname + (q.length ? '?' + q.join('&') : '') + location.hash); } catch (e) {}
  }

  function toB64url(str) {
    var bytes = new TextEncoder().encode(str);
    var bin = '';
    for (var i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromB64url(code) {
    var b = code.replace(/-/g, '+').replace(/_/g, '/');
    while (b.length % 4) b += '=';
    var bin = atob(b);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }

  function linkCode(sn) {
    return 'j' + toB64url(JSON.stringify([
      LINK_VERSION, sn.s, sn.f, sn.lay, sn.lt ? 1 : 0,
      sn.file ? [sn.file.input, sn.file.name] : 0
    ]));
  }

  function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }

  function okKey(k) {
    return KEY_RE.test(k) && k !== '__proto__' && k !== 'constructor' && k !== 'prototype';
  }

  /* Values the page itself defines: every data-* value and option value in
     the designer, plus the page's own starting design */
  function pageValues(d) {
    var seen = {};
    var els = d.root.querySelectorAll('*');
    for (var i = 0; i < els.length; i++) {
      var at = els[i].attributes;
      for (var j = 0; j < at.length; j++) {
        if (at[j].name.indexOf('data-') === 0) seen[at[j].value] = 1;
      }
      if (els[i].tagName === 'OPTION') seen[els[i].value] = 1;
    }
    (function walk(v) {
      if (typeof v === 'string') seen[v] = 1;
      else if (v && typeof v === 'object') for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) walk(v[k]);
    })(d.base ? d.base.s : null);
    return seen;
  }

  /* One link value, deep. undefined = not allowed. */
  function cleanValue(v, known, depth) {
    if (v === null || typeof v === 'boolean') return v;
    if (typeof v === 'number') return isFinite(v) ? v : undefined;
    if (typeof v === 'string') {
      if (Object.prototype.hasOwnProperty.call(known, v)) return v;
      return PLAIN_RE.test(v) && !SCRIPT_RE.test(v) ? v : undefined;
    }
    if (depth > 4 || typeof v !== 'object') return undefined;
    var out, k, c, n = 0;
    if (Array.isArray(v)) {
      if (v.length > 60) return undefined;
      out = [];
      for (k = 0; k < v.length; k++) {
        c = cleanValue(v[k], known, depth + 1);
        if (c === undefined) return undefined;
        out.push(c);
      }
      return out;
    }
    out = {};
    for (k in v) {
      if (!Object.prototype.hasOwnProperty.call(v, k)) continue;
      if (!okKey(k) || ++n > 60) return undefined;
      c = cleanValue(v[k], known, depth + 1);
      if (c === undefined) return undefined;
      out[k] = c;
    }
    return out;
  }

  /* Link code -> a saved design this page can use, or null.
     dropped lists what was left out (tests). */
  function readLink(d, code) {
    var a;
    if (!code || code.charAt(0) !== 'j' || code.length > 20000) return null;
    try { a = JSON.parse(fromB64url(code.slice(1))); } catch (e) { return null; }
    if (!Array.isArray(a) || a[0] !== LINK_VERSION || !isObj(a[1]) || !isObj(a[2])) return null;

    var st = d.st;
    var known = pageValues(d);
    var dropped = [];
    var s = {};
    var k, v, c;

    /* Button choices: one of the page's own buttons for that choice */
    var choice = {};
    for (var attr in PRESS) choice[PRESS[attr]] = attr;

    for (k in a[1]) {
      if (!Object.prototype.hasOwnProperty.call(a[1], k)) continue;
      v = a[1][k];
      if (!okKey(k) || SKIP_RE.test(k) || !Object.prototype.hasOwnProperty.call(st, k)) { dropped.push('s.' + k); continue; }
      var cur = st[k];
      if (cur !== null && cur !== undefined && v !== null) {
        if (typeof v !== typeof cur || Array.isArray(v) !== Array.isArray(cur)) { dropped.push('s.' + k); continue; }
      }
      if (typeof v === 'string' && choice[k] && d.root.querySelector('button[data-' + choice[k] + ']')) {
        var hit = false;
        var bs = d.root.querySelectorAll('button[data-' + choice[k] + ']');
        for (var i = 0; i < bs.length; i++) if (bs[i].getAttribute('data-' + choice[k]) === v) hit = true;
        if (!hit && !(d.base && d.base.s[k] === v)) { dropped.push('s.' + k); continue; }
        s[k] = v;
        continue;
      }
      c = cleanValue(v, known, 0);
      if (c === undefined) { dropped.push('s.' + k); continue; }
      s[k] = c;
    }

    /* Typed fields: the page's own fields, text without < > ` */
    var f = {};
    for (k in a[2]) {
      if (!Object.prototype.hasOwnProperty.call(a[2], k)) continue;
      v = a[2][k];
      var el = okKey(k) ? document.getElementById(k) : null;
      if (!el || !d.root.contains(el)) { dropped.push('f.' + k); continue; }
      if (typeof v === 'boolean') f[k] = v;
      else if (typeof v === 'string' || typeof v === 'number') f[k] = String(v).replace(/[<>`]/g, '').slice(0, 500);
      else dropped.push('f.' + k);
    }

    var lay = {};
    if (isObj(a[3])) {
      c = cleanValue(a[3], {}, 0);
      if (c === undefined) dropped.push('lay');
      else lay = c;
    }

    var file = null;
    if (Array.isArray(a[5]) && typeof a[5][0] === 'string') {
      var inp = okKey(a[5][0]) ? document.getElementById(a[5][0]) : null;
      if (inp && d.root.contains(inp) && inp.type === 'file') {
        file = { input: inp.id, name: String(a[5][1] || '').replace(/[^\w .()+-]/g, '').slice(0, 100) || 'design' };
      } else {
        dropped.push('file');
      }
    }

    return { sn: { v: VERSION, s: s, f: f, lay: lay, lt: !!a[4], file: file }, dropped: dropped };
  }

  /* The order's uploaded file, from Shopify's file server only */
  function fetchFile(url, name) {
    if (!url || url.indexOf(FILE_HOST) !== 0 || !window.fetch) return Promise.resolve(null);
    var get = fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('file ' + r.status);
      return r.blob();
    }).then(function (blob) {
      var nm = name || decodeURIComponent(url.split('?')[0].split('/').pop() || 'design');
      return readBuf(blob).then(function (buf) {
        return { name: nm, type: blob.type || '', lastModified: Date.now(), size: blob.size, buf: buf };
      });
    });
    return Promise.race([get, new Promise(function (r) { setTimeout(function () { r(null); }, LINK_FILE_WAIT); })])
      .catch(function () { return null; });
  }

  /* ---------- one designer ---------- */

  function Designer(cfg) {
    var d = this;
    d.root = typeof cfg.root === 'string' ? document.getElementById(cfg.root) : cfg.root;
    d.st = cfg.st;
    d.renderFn = cfg.render;
    d.sync = cfg.sync || null;
    d.ui = cfg.ui || null;
    d.ready = cfg.ready || null;
    d.key = pageKey();
    d.busy = 0;
    d.live = false;
    d.touched = false;
    d.edits = 0;
    d.lastJ = '';
    d.baseJ = '';
    d.fileSig = '';
    d.savedAt = 0;
    d.timer = 0;
  }

  Designer.prototype.render = function () {
    try { this.renderFn(); } catch (e) {}
  };

  /* After a restore: press the buttons that match st, the way a click
     would have. A group is left alone when no button matches. */
  Designer.prototype.press = function () {
    var d = this;
    var st = d.st;
    if (d.ui) { try { d.ui(); } catch (e) {} }
    var seen = [];
    var btns = d.root.querySelectorAll('button[aria-pressed]');
    for (var i = 0; i < btns.length; i++) {
      var box = btns[i].parentNode;
      if (!box || seen.indexOf(box) !== -1) continue;
      seen.push(box);
      var attr = null;
      var key = null;
      for (var a in PRESS) {
        if (btns[i].hasAttribute('data-' + a)) { attr = 'data-' + a; key = PRESS[a]; break; }
      }
      /* Upload color grids: same data-c, the grid says which color */
      if (!attr && btns[i].hasAttribute('data-c') && box.id) {
        attr = 'data-c';
        key = /-bg$/.test(box.id) ? 'bg' : /-tx$/.test(box.id) ? 'tx' : null;
      }
      if (!attr || !key || !Object.prototype.hasOwnProperty.call(st, key)) continue;
      var want = String(st[key]);
      var group = box.querySelectorAll(':scope > button[' + attr + ']');
      var hit = false;
      for (var j = 0; j < group.length; j++) {
        if (group[j].getAttribute(attr) === want) hit = true;
      }
      if (!hit) continue;
      for (j = 0; j < group.length; j++) {
        group[j].setAttribute('aria-pressed', group[j].getAttribute(attr) === want ? 'true' : 'false');
      }
    }

    /* Street with image: one group sets two choices, image area + border
       (data-bp="panel-no" etc.) */
    var bp = d.root.querySelectorAll('button[data-bp]');
    if (bp.length && 'imgArea' in st && 'border' in st) {
      var wantBp = (st.imgArea === 'panel' ? 'panel' : 'inside') + '-' + (st.border === 'outside' ? 'border' : 'no');
      var bpHit = false;
      for (i = 0; i < bp.length; i++) if (bp[i].getAttribute('data-bp') === wantBp) bpHit = true;
      for (i = 0; bpHit && i < bp.length; i++) {
        bp[i].setAttribute('aria-pressed', bp[i].getAttribute('data-bp') === wantBp ? 'true' : 'false');
      }
    }
  };

  /* Typed fields only: customer-facing properties, quantity, checkboxes */
  Designer.prototype.fields = function () {
    var out = [];
    var els = this.root.querySelectorAll('input, textarea, select');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var type = (el.type || '').toLowerCase();
      var name = el.name || '';
      if (!el.id || type === 'hidden' || type === 'file' || type === 'button' || type === 'submit') continue;
      if (name.indexOf('properties[_') === 0) continue;
      if (name.indexOf('properties[') === 0 || name === 'quantity' || type === 'checkbox') out.push(el);
    }
    return out;
  };

  /* Customer file inputs (not the add-to-cart _Preview image) */
  Designer.prototype.fileInputs = function () {
    var out = [];
    var els = this.root.querySelectorAll('input[type="file"]');
    for (var i = 0; i < els.length; i++) {
      var name = els[i].name || '';
      if (els[i].id && name.indexOf('properties[_') !== 0) out.push(els[i]);
    }
    return out;
  };

  Designer.prototype.withFile = function () {
    var ins = this.fileInputs();
    for (var i = 0; i < ins.length; i++) {
      if (ins[i].files && ins[i].files.length) return ins[i];
    }
    return null;
  };

  Designer.prototype.snap = function () {
    var st = this.st;
    var s = {};
    for (var k in st) {
      if (!Object.prototype.hasOwnProperty.call(st, k) || SKIP_RE.test(k)) continue;
      if (st[k] === undefined || !plain(st[k])) continue;
      s[k] = clone(st[k]);
    }
    var f = {};
    this.fields().forEach(function (el) {
      f[el.id] = el.type === 'checkbox' ? !!el.checked : el.value;
    });
    var inp = this.withFile();
    var file = null;
    if (inp) {
      var fl = inp.files[0];
      file = { input: inp.id, name: fl.name, size: fl.size, type: fl.type, lastModified: fl.lastModified };
    }
    return {
      v: VERSION,
      s: s,
      f: f,
      lay: plain(st.lay) ? clone(st.lay || {}) : {},
      lt: !!st.layTouched,
      file: file
    };
  };

  Designer.prototype.json = function (sn) {
    return JSON.stringify([sn.s, sn.f, sn.lay, sn.lt, sn.file && fileSig(sn.file)]);
  };

  /* Put a saved design back. rec = stored file record (or null). */
  Designer.prototype.apply = function (sn, rec) {
    var d = this;
    var st = d.st;
    d.busy++;

    var k;
    for (k in sn.s) {
      if (!Object.prototype.hasOwnProperty.call(sn.s, k) || SKIP_RE.test(k)) continue;
      if (!Object.prototype.hasOwnProperty.call(st, k)) continue;
      st[k] = clone(sn.s[k]);
    }

    var changed = [];
    for (k in sn.f) {
      if (!Object.prototype.hasOwnProperty.call(sn.f, k)) continue;
      var el = document.getElementById(k);
      if (!el || !d.root.contains(el)) continue;
      if (el.type === 'checkbox') {
        if (el.checked !== !!sn.f[k]) { el.checked = !!sn.f[k]; changed.push(el); }
      } else if (el.value !== sn.f[k]) {
        var before = el.value;
        el.value = sn.f[k];
        if (el.tagName === 'SELECT' && el.value !== sn.f[k]) el.value = before;
        else changed.push(el);
      }
    }

    /* No "layout reset" notes while the words and size change */
    st.lay = {};
    if (d.sync) { try { d.sync(); } catch (e) {} }
    /* "No symbol" on a page that opens with one: the diamond keeps the
       symbol picture in graphic*, so clear it the way its No symbol
       button does (a saved upload comes back below) */
    if (st.symbol === '' && st.graphicUrl && !sn.file) {
      st.graphicName = '';
      st.graphicUrl = '';
      st.graphicPreviewable = false;
      if (typeof st.graphicToken === 'number') st.graphicToken++;
    }
    /* Same signal as typing or picking, so each designer reads its own
       fields its own way (quantity, fonts, checkboxes) */
    changed.forEach(function (el) {
      fire(el, el.type === 'checkbox' || el.tagName === 'SELECT' ? 'change' : 'input');
      if (el.name === 'quantity') fire(el, 'change');
    });
    d.render();

    return d.putFile(sn, rec).then(function (fileOk) {
      /* Layout last: the designer has now checked it against the words,
         size and file, so the saved spots stay */
      st.lay = clone(sn.lay || {});
      if (Object.prototype.hasOwnProperty.call(st, 'layTouched')) st.layTouched = !!sn.lt;
      d.render();

      /* A size or sheeting that is no longer sold: back to the page start */
      var vid = d.root.querySelector('input[name="id"]');
      if (vid && !vid.value && d.base && d.base.s.size !== undefined && st.size !== d.base.s.size) {
        st.size = d.base.s.size;
        d.render();
      }
      d.press();
      d.busy--;
      return fileOk;
    }, function () {
      d.busy--;
      return false;
    });
  };

  /* Logo or design file back into its own input, through the designer's
     own "file picked" step. Resolves false when the browser cannot. */
  Designer.prototype.putFile = function (sn, rec) {
    var d = this;
    var st = d.st;
    var want = sn.file;
    var jobs = [];
    var ok = true;

    d.fileInputs().forEach(function (inp) {
      var has = inp.files && inp.files.length;

      if (want && inp.id === want.input) {
        if (!rec || !rec.buf) { ok = false; return; }
        var file;
        try { file = new File([rec.buf], rec.name, { type: rec.type || '', lastModified: rec.lastModified || Date.now() }); }
        catch (e) { ok = false; return; }
        if (has && fileSig(inp.files[0]) === fileSig(file)) return;
        try {
          var dt = new DataTransfer();
          dt.items.add(file);
          inp.files = dt.files;
        } catch (e2) { ok = false; return; }
        if (!inp.files || !inp.files.length) { ok = false; return; }
        fire(inp, 'change');
        jobs.push(wait(function () {
          var nm = 'graphicName' in st ? st.graphicName : st.imageName;
          if (nm !== file.name) return false;
          if (/-in-design$/.test(inp.id) && 'designMeasured' in st) return !!st.designMeasured;
          if ('graphicPreviewable' in st) return !st.graphicPreviewable || !!st.graphicUrl;
          return true;
        }, FILE_WAIT));
      } else if (has) {
        /* A file the saved design did not have: removed the designer's way */
        inp.value = '';
        fire(inp, 'change');
      }
    });

    return Promise.all(jobs).then(function () { return ok; });
  };

  Designer.prototype.schedule = function (e) {
    var d = this;
    if (d.busy) return;
    if (e && e.target && e.target.closest && e.target.closest('.rs-czp-head')) return;
    if (e && e.isTrusted) d.edits++;
    if (e && e.isTrusted && !d.live) d.touched = true;
    if (!d.live) return;
    if (e && e.isTrusted && d.line && !d.line.hidden) { d.hiddenBy = e.type; hideRestored(d); }
    clearTimeout(d.timer);
    d.timer = setTimeout(function () { d.save(); }, SAVE_DELAY);
  };

  Designer.prototype.save = function () {
    var d = this;
    clearTimeout(d.timer);
    if (d.busy || !d.live) return Promise.resolve();

    var sn = d.snap();
    var j = d.json(sn);
    if (j === d.lastJ) return Promise.resolve();
    d.lastJ = j;

    /* Back to how the page starts: nothing to keep */
    if (j === d.baseJ) {
      d.fileSig = '';
      return store().then(function (s) { return s.del(d.key); }).then(function () { showSaved(d, 0); }, function () {});
    }

    sn.t = Date.now();
    var inp = d.withFile();
    var fl = inp && inp.files[0];

    return store().then(function (s) {
      var jobs = [s.put(d.key, sn)];
      if (fl) {
        var sig = fileSig(fl);
        if (sig !== d.fileSig) {
          d.fileSig = sig;
          jobs.push(readBuf(fl).then(function (buf) {
            return s.putFile(d.key, { name: fl.name, type: fl.type, lastModified: fl.lastModified, size: fl.size, buf: buf });
          }));
        }
      } else if (d.fileSig) {
        d.fileSig = '';
        jobs.push(s.delFile(d.key));
      }
      return Promise.all(jobs);
    }).then(function () { showSaved(d, sn.t); }, function () {});
  };

  /* Start over: the page's own starting design, saved copy removed */
  Designer.prototype.reset = function () {
    var d = this;
    if (!d.base) return Promise.resolve();
    clearTimeout(d.timer);
    hideRestored(d);
    return d.apply(d.base, null).then(function () {
      d.lastJ = d.baseJ;
      d.fileSig = '';
      return store().then(function (s) { return s.del(d.key); });
    }).then(function () { showSaved(d, 0); }, function () { showSaved(d, 0); });
  };

  /* Part 3: the add-to-cart form and its hidden design link */
  Designer.prototype.form = function () {
    var vid = this.root.querySelector('input[name="id"]');
    return (vid && vid.form) || this.root.querySelector('form');
  };

  Designer.prototype.link = function () {
    return location.origin + '/products/' + encodeURIComponent(this.key) + '?design=' + linkCode(this.snap());
  };

  Designer.prototype.fillLink = function () {
    var form = this.form();
    if (!form) return;
    var inp = form.querySelector('input[data-rs-design-link]');
    if (!inp) {
      inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = LINK_NAME;
      inp.setAttribute('data-rs-design-link', '');
      form.appendChild(inp);
    }
    try { inp.value = this.link(); } catch (e) { inp.parentNode.removeChild(inp); }
  };

  /* Opened from "Order this sign again": that design instead of the device
     copy. Resolves true when the sign is back. */
  Designer.prototype.fromLink = function () {
    var d = this;
    LINK.used = true;
    var got = readLink(d, LINK.code);
    if (!got) return Promise.resolve(false);
    var sn = got.sn;
    var fileJob = sn.file ? fetchFile(LINK.file, sn.file.name) : Promise.resolve(null);
    return fileJob.then(function (rec) {
      return d.apply(sn, rec).then(function (fileOk) {
        cleanAddress();
        showRestored(d, sn.file ? !!rec && fileOk : true, true);
        return true;
      });
    });
  };

  /* ---------- page bits ---------- */

  function setBtnText(id, text) {
    var b = document.getElementById(id);
    var sp = b && b.querySelector('span');
    if (!sp) return;
    if (!sp.hasAttribute('data-rs-text')) sp.setAttribute('data-rs-text', sp.textContent);
    sp.textContent = text || sp.getAttribute('data-rs-text');
  }

  function showSaved(d, t) {
    d.savedAt = t || 0;
    setBtnText('rs-czp-btn', t ? 'Continue my design' : '');
    setBtnText('rs-czp-sbar-btn', t ? 'Continue my design' : '');

    var note = document.querySelector('.rs-czp-top .rs-czp-note');
    if (!note) return;
    if (!note.hasAttribute('data-rs-text')) note.setAttribute('data-rs-text', note.textContent);

    if (!t) {
      note.classList.remove('rs-czp-note--saved');
      note.textContent = note.getAttribute('data-rs-text');
      return;
    }

    note.classList.add('rs-czp-note--saved');
    note.innerHTML = '';
    note.appendChild(document.createTextNode('Saved on this ' + deviceWord() + ' ' + when(t) + '. '));
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rs-czp-linkbtn';
    btn.textContent = 'Start a new design';
    btn.addEventListener('click', function () {
      d.reset().then(function () {
        var open = document.getElementById('rs-czp-btn');
        if (open) open.click();
      });
    });
    note.appendChild(btn);
  }

  function showRestored(d, fileOk, fromLink) {
    var head = d.root.querySelector('.rs-czp-head');
    if (!head) {
      /* A quick restore can finish before the snippet adds the window's
         title bar (page load end). Show the line then, unless the
         customer has changed something by that time. */
      var edits = d.edits;
      wait(function () { return d.root.querySelector('.rs-czp-head'); }, READY_WAIT).then(function (ok) {
        if (ok && d.edits === edits) showRestored(d, fileOk, fromLink);
      });
      return;
    }
    var line = d.line;
    if (!line) {
      line = document.createElement('div');
      line.className = 'rs-czp-restored';
      line.setAttribute('role', 'status');
      head.appendChild(line);
      d.line = line;
    }
    line.innerHTML = CHECK_SVG + '<span class="rs-czp-restored__t"></span>';
    var said = fromLink ? 'Your sign from your order is loaded.' : 'Picked up where you left off.';
    line.querySelector('.rs-czp-restored__t').textContent = fileOk === false
      ? said + ' Please add your file again.'
      : said;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rs-czp-linkbtn';
    btn.textContent = 'Start over';
    btn.addEventListener('click', function () { d.reset(); });
    line.appendChild(btn);
    line.hidden = false;
    head.classList.add('rs-czp-head--note');
  }

  function hideRestored(d) {
    if (!d.line || d.line.hidden) return;
    d.line.hidden = true;
    var head = d.root.querySelector('.rs-czp-head');
    if (head) head.classList.remove('rs-czp-head--note');
  }

  /* ---------- start ---------- */

  function start(cfg) {
    if (!cfg || !cfg.st || typeof cfg.render !== 'function') return;
    var d = new Designer(cfg);
    if (!d.root) return;
    window.RSDesignSave.designers.push(d);

    ['input', 'change', 'click', 'pointerup', 'keyup'].forEach(function (type) {
      d.root.addEventListener(type, function (e) { d.schedule(e); }, true);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') d.save();
    });
    window.addEventListener('pagehide', function () { d.save(); });
    /* Capture phase: before the designer's own submit step reads the form */
    document.addEventListener('submit', function (e) {
      if (e.target === d.form()) d.fillLink();
    }, true);

    var linked = false;

    function fromDevice() {
      return store().then(function (s) {
        s.prune().catch(function () {});
        return s.get(d.key).then(function (sn) {
          if (!sn || sn.v !== VERSION || !sn.t || Date.now() - sn.t > KEEP_MS) return null;
          if (d.touched) return null;
          return (sn.file ? s.getFile(d.key) : Promise.resolve(null)).then(function (rec) {
            if (d.touched) return null;
            return d.apply(sn, rec).then(function (fileOk) {
              d.lastJ = d.json(d.snap());
              if (rec && fileOk) d.fileSig = fileSig(rec);
              showSaved(d, sn.t);
              showRestored(d, fileOk);
            });
          });
        });
      });
    }

    wait(function () { return !d.ready || d.ready(); }, READY_WAIT)
      .then(fontsIn)
      .then(function () {
        d.base = d.snap();
        d.baseJ = d.json(d.base);
        d.lastJ = d.baseJ;

        /* "Order this sign again" link: that sign, kept on this device after */
        if (LINK.code && !LINK.used) {
          return d.fromLink().then(function (ok) {
            if (ok) linked = true;
            else return fromDevice();
          }, fromDevice);
        }

        /* "Customize this sign" links load that sign instead */
        if (/[?&]rs_[a-z0-9]+=/i.test(location.search)) return null;

        return fromDevice();
      })
      .catch(function () {})
      .then(function () {
        d.live = true;
        if (linked) {
          d.lastJ = '';
          d.fileSig = '';
          d.save();
        }
      });
  }

  window.RSDesignSave = {
    designers: [], start: start, version: VERSION,
    /* tests */
    readLink: readLink, linkCode: linkCode
  };

  var q = window.RSDesignQueue || [];
  for (var i = 0; i < q.length; i++) start(q[i]);
  window.RSDesignQueue = { push: start };
})();
